<template>
  <v-dialog
    v-model="dialog"
    width="600"
    scrollable
    @keydown.esc="dialog = false"
  >
    <v-card class="quick-actions-card">
      <!-- Search input -->
      <v-card-title class="pa-0">
        <v-text-field
          ref="searchField"
          v-model="search"
          placeholder="Type a command or search… (e.g. invoice, client, supplier)"
          prepend-inner-icon="mdi-lightning-bolt"
          solo
          flat
          hide-details
          clearable
          autofocus
          class="quick-actions-search"
          @input="highlighted = 0"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="selectHighlighted"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Action list -->
      <v-card-text class="pa-0 quick-actions-list">
        <v-list dense>
          <template v-if="filteredActions.length">
            <v-list-item
              v-for="(action, index) in filteredActions"
              :key="action.id"
              :class="{ 'quick-actions-active': index === highlighted }"
              @click="run(action)"
              @mousemove="highlighted = index"
            >
              <v-list-item-icon>
                <v-icon :color="action.color || 'primary'">{{
                  action.icon
                }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ action.title }}</v-list-item-title>
                <v-list-item-subtitle v-if="action.subtitle">{{
                  action.subtitle
                }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="action.badge">
                <v-chip x-small label outlined>{{ action.badge }}</v-chip>
              </v-list-item-action>
            </v-list-item>
          </template>
          <v-list-item v-else>
            <v-list-item-content>
              <v-list-item-title class="text--secondary">
                No matching actions for "{{ search }}"
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Footer hint -->
      <v-card-actions class="quick-actions-footer text-caption py-2 px-4">
        <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
        <span class="ml-4"><kbd>↵</kbd> select</span>
        <span class="ml-4"><kbd>esc</kbd> close</span>
        <v-spacer></v-spacer>
        <span>{{ shortcutLabel }}</span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "QuickActions",
  data() {
    return {
      dialog: false,
      search: "",
      highlighted: 0,
      isMac:
        typeof navigator !== "undefined" &&
        /Mac|iPhone|iPad/.test(navigator.platform || ""),
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      hasPermission: "permission/hasPermission",
    }),
    shortcutLabel() {
      return this.isMac ? "⌘ + S" : "Ctrl + S";
    },
    // Full action catalog. Each action declares the Role Restrictions
    // `permission` key it maps to; accounts without hats see nothing.
    actions() {
      return [
        // --- Create shortcuts ---
        {
          id: "new-client",
          title: "Add New Client",
          subtitle: "Create a new customer account",
          icon: "mdi-account-plus",
          color: "green",
          badge: "New",
          permission: "accounts.add_customer",
          run: () => this.go("/accounts", { quick: "add-client" }),
        },
        {
          id: "new-invoice",
          title: "Add New Invoice",
          subtitle: "Create a new invoice",
          icon: "mdi-receipt-text-plus",
          color: "green",
          badge: "New",
          permission: "add_invoice.access",
          run: () => this.go("/invoices", { quick: "add-invoice" }),
        },
        {
          id: "new-downpayment",
          title: "Add Down Payment",
          subtitle: "Record a customer down payment",
          icon: "mdi-cash-plus",
          color: "orange",
          badge: "New",
          permission: "down_payments.access",
          run: () => this.go("/invoices", { quick: "add-downpayment" }),
        },
        // --- Navigation ---
        {
          id: "go-supplier-po",
          title: "Supplier Purchase Orders",
          subtitle: "Go to supplier PO page",
          icon: "mdi-truck-delivery-outline",
          permission: "route.supplier_purchase_orders",
          run: () => this.go("/supplier_purchase_orders"),
        },
        {
          id: "go-dashboard",
          title: "Dashboard",
          subtitle: "Go to dashboard",
          icon: "mdi-view-dashboard",
          permission: "route.dashboard",
          run: () => this.go("/"),
        },
        {
          id: "go-accounts",
          title: "Accounts / Clients",
          subtitle: "Go to accounts page",
          icon: "mdi-account-multiple",
          permission: "route.accounts",
          run: () => this.go("/accounts"),
        },
        {
          id: "go-invoices",
          title: "Invoices",
          subtitle: "Go to invoices page",
          icon: "mdi-receipt-text",
          permission: "route.invoices",
          run: () => this.go("/invoices"),
        },
        {
          id: "go-quotations",
          title: "Quotations",
          subtitle: "Go to quotations page",
          icon: "mdi-file-document-outline",
          permission: "route.quotations",
          run: () => this.go("/quotations"),
        },
        {
          id: "go-purchase-orders",
          title: "Government Purchase Orders",
          subtitle: "Go to purchase orders page",
          icon: "mdi-cart-outline",
          permission: "route.customer_purchase_orders",
          run: () => this.go("/customer_purchase_orders"),
        },
        {
          id: "go-units",
          title: "Units",
          subtitle: "Go to units page",
          icon: "mdi-desktop-tower-monitor",
          permission: "route.units",
          run: () => this.go("/units"),
        },
        {
          id: "go-items",
          title: "Inventory Management",
          subtitle: "Go to items / inventory page",
          icon: "mdi-package-variant",
          permission: "route.items",
          run: () => this.go("/items"),
        },
        {
          id: "go-rma",
          title: "RMA Items",
          subtitle: "Go to RMA management",
          icon: "mdi-truck-cargo-container",
          permission: "route.rma",
          run: () => this.go("/rma"),
        },
        {
          id: "go-inventory-reports",
          title: "Inventory Reports",
          subtitle: "Go to inventory reports",
          icon: "mdi-chart-bar",
          permission: "route.inventory_reports",
          run: () => this.go("/inventory_reports"),
        },
        {
          id: "go-services",
          title: "Services",
          subtitle: "Go to services page",
          icon: "mdi-tools",
          permission: "route.services",
          run: () => this.go("/services"),
        },
        {
          id: "go-service-reports",
          title: "Service Reports",
          subtitle: "Go to service reports",
          icon: "mdi-chart-box",
          permission: "route.service_reports",
          run: () => this.go("/service_reports"),
        },
        {
          id: "go-suppliers",
          title: "Suppliers",
          subtitle: "Go to suppliers page",
          icon: "mdi-store",
          permission: "route.suppliers",
          run: () => this.go("/suppliers"),
        },
        {
          id: "go-branches",
          title: "Branches",
          subtitle: "Go to branches page",
          icon: "mdi-office-building",
          permission: "route.branches",
          run: () => this.go("/branches"),
        },
        {
          id: "go-employees",
          title: "Employees and Wages",
          subtitle: "Go to employees page",
          icon: "mdi-badge-account-horizontal",
          permission: "route.employees",
          run: () => this.go("/employees"),
        },
        {
          id: "go-task-management",
          title: "Task Management",
          subtitle: "Go to task management",
          icon: "mdi-clipboard-check-outline",
          permission: "route.task_management",
          run: () => this.go("/task_management"),
        },
        {
          id: "go-user-accounts",
          title: "User Accounts",
          subtitle: "Go to user accounts",
          icon: "mdi-account-cog",
          permission: "route.user_accounts",
          run: () => this.go("/user_accounts"),
        },
        {
          id: "go-role-restrictions",
          title: "Roles and Permissions",
          subtitle: "Go to roles & permissions (hat bundle editor)",
          icon: "mdi-shield-account",
          permission: "route.role_restrictions",
          run: () => this.go("/role_restrictions"),
        },
        {
          id: "go-admin-settings",
          title: "Admin Settings",
          subtitle: "Go to admin settings hub",
          icon: "mdi-cog",
          permission: "route.admin_settings",
          run: () => this.go("/admin_settings"),
        },
        {
          id: "go-sales",
          title: "Sales",
          subtitle: "Go to sales dashboard & leads",
          icon: "mdi-chart-box",
          permission: "route.sales_dashboard",
          run: () => this.go("/sales"),
        },
        {
          id: "go-leads",
          title: "Leads",
          subtitle: "Go to leads CRM",
          icon: "mdi-account-search",
          permission: "route.leads",
          run: () => this.go("/leads"),
        },
        {
          id: "go-ar-aging",
          title: "AR Aging",
          subtitle: "Go to AR aging dashboard",
          icon: "mdi-account-clock-outline",
          permission: "ar.view_aging_dashboard",
          run: () => this.go("/ar_aging"),
        },
        {
          id: "go-recurring-invoices",
          title: "Recurring Invoices",
          subtitle: "Go to recurring invoices",
          icon: "mdi-invoice-text-clock",
          permission: "route.recurring_invoices",
          run: () => this.go("/recurring_invoices"),
        },
        {
          id: "go-customer-aftercare",
          title: "Customer Aftercare",
          subtitle: "Go to installed systems & maintenance",
          icon: "mdi-face-agent",
          permission: "route.installed_systems",
          run: () => this.go("/installed_systems"),
        },
        {
          id: "go-purchase-requests",
          title: "Purchase Requests",
          subtitle: "Go to purchase requests (internal PO)",
          icon: "mdi-clipboard-list-outline",
          permission: "route.internal_purchase_orders",
          run: () => this.go("/internal_purchase_orders"),
        },
        {
          id: "go-warranty-policies",
          title: "Warranty Policies",
          subtitle: "Go to warranty policies",
          icon: "mdi-shield-check-outline",
          permission: "route.warranty_policies",
          run: () => this.go("/warranty_policies"),
        },
        {
          id: "go-batch-import",
          title: "Batch Imports",
          subtitle: "Go to batch imports",
          icon: "mdi-database-import",
          permission: "route.batch_import",
          run: () => this.go("/batch_import"),
        },
        {
          id: "go-printable-forms",
          title: "Printable Forms",
          subtitle: "Go to printable forms",
          icon: "mdi-printer",
          permission: "route.printable_forms",
          run: () => this.go("/printable-forms"),
        },
      ];
    },
    availableActions() {
      return this.actions.filter((a) => this.canAccess(a));
    },
    filteredActions() {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return this.availableActions;
      return this.availableActions.filter((a) => {
        const haystack = (a.title + " " + (a.subtitle || "")).toLowerCase();
        return haystack.includes(q);
      });
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        this.search = "";
        this.highlighted = 0;
        this.focusSearch();
      }
    },
  },
  created() {
    window.addEventListener("keydown", this.onGlobalKeydown);
    // Allow other components (e.g. the app bar button) to open the palette.
    this.$root.$on("open-quick-actions", this.open);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onGlobalKeydown);
    this.$root.$off("open-quick-actions", this.open);
  },
  methods: {
    // Decide whether the current user may see/run an action: the action's
    // permission key must be in the user's hat-granted set.
    canAccess(action) {
      if (!this.user) return false;
      return this.hasPermission(action.permission);
    },
    open() {
      this.dialog = true;
    },
    toggle() {
      this.dialog = !this.dialog;
    },
    onGlobalKeydown(e) {
      // Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      const isSaveCombo =
        (e.metaKey || e.ctrlKey) && e.key && e.key.toLowerCase() === "s";
      if (isSaveCombo) {
        e.preventDefault();
        e.stopPropagation();
        // Don't trigger when there is no logged-in user.
        if (this.user) this.toggle();
      }
    },
    // Focus the search field once the dialog's open transition has finished.
    // $nextTick fires too early (the input isn't focusable yet), so retry.
    focusSearch(attempt = 0) {
      this.$nextTick(() => {
        const field = this.$refs.searchField;
        if (field && field.focus) {
          field.focus();
        } else if (attempt < 10) {
          setTimeout(() => this.focusSearch(attempt + 1), 50);
        }
      });
    },
    // Move the highlight up (-1) or down (+1), wrapping around the list.
    move(dir) {
      const len = this.filteredActions.length;
      if (!len) return;
      this.highlighted = (this.highlighted + dir + len) % len;
    },
    // Run the currently highlighted action (Enter key).
    selectHighlighted() {
      const action = this.filteredActions[this.highlighted];
      if (action) this.run(action);
    },
    run(action) {
      this.dialog = false;
      // Defer so the dialog close animation doesn't fight the route change.
      this.$nextTick(() => action.run());
    },
    // Navigate, optionally passing a `quick` query consumed by the target page.
    go(path, query = {}) {
      const target = { path, query };
      const current = this.$route;
      const sameRoute = current.path === path && !Object.keys(query).length;
      if (sameRoute) return; // already there, nothing to do
      this.$router.push(target).catch(() => {});
    },
  },
};
</script>

<style scoped>
.quick-actions-search >>> input {
  font-size: 1.1rem;
}
.quick-actions-list {
  max-height: 55vh;
}
.quick-actions-active {
  background-color: rgba(var(--v-primary-base), 0.12);
}
.theme--dark .quick-actions-active {
  background-color: rgba(255, 255, 255, 0.08);
}
.theme--light .quick-actions-active {
  background-color: rgba(0, 0, 0, 0.05);
}
.quick-actions-footer kbd {
  background: rgba(127, 127, 127, 0.2);
  border-radius: 4px;
  padding: 1px 6px;
  margin: 0 2px;
  font-size: 0.75rem;
}
</style>
